// Telefoonnummers: landkeuze (standaard België, +32) + controle of het nummer
// juist gevormd is voor dat land, en omzetten naar één vaste weergave
// ("+32 470 12 34 56").
//
// LET OP: backend/phoneValidation.js is een kopie van dit bestand (CommonJS) -
// de server controleert het nummer bij accountactivatie nog eens. Pas je hier
// iets aan, pas het daar dan ook aan (backend/scripts/ of de unit-test in
// src/utils/phone.test.js controleert beide met dezelfde gevallen).

// patterns = geldige "nationale significante nummers": het nummer zonder
// landcode en zonder het (in veel landen gebruikte) beginnende 0.
// trunk = true: een beginnende 0 wordt genegeerd (bv. 0470 12 34 56 -> 470...).
// Italië houdt zijn 0 bij vaste nummers wél, vandaar trunk: false.
export const PHONE_COUNTRIES = [
  { code: 'BE', dial: '32', trunk: true, patterns: [/^4[5-9]\d{7}$/, /^[1-9]\d{7}$/], groups: (n) => (n.length === 9 ? [3, 2, 2, 2] : /^[2349]/.test(n) ? [1, 3, 2, 2] : [2, 2, 2, 2]) },
  { code: 'NL', dial: '31', trunk: true, patterns: [/^[1-9]\d{8}$/], groups: (n) => (n[0] === '6' ? [1, 2, 2, 2, 2] : [2, 3, 4]) },
  { code: 'FR', dial: '33', trunk: true, patterns: [/^[1-9]\d{8}$/], groups: () => [1, 2, 2, 2, 2] },
  { code: 'DE', dial: '49', trunk: true, patterns: [/^1[5-7]\d{8,9}$/, /^[2-9]\d{5,12}$/], groups: () => [3] },
  { code: 'LU', dial: '352', trunk: false, patterns: [/^6\d{8}$/, /^[2-579]\d{5,7}$/], groups: (n) => (n.length === 9 ? [3, 3, 3] : [2, 2, 2, 2]) },
  { code: 'GB', dial: '44', trunk: true, patterns: [/^7[1-9]\d{8}$/, /^[1-3]\d{9}$/, /^8\d{9}$/], groups: () => [4, 6] },
  { code: 'IE', dial: '353', trunk: true, patterns: [/^8[3-9]\d{7}$/, /^[1-79]\d{7,8}$/], groups: () => [2, 3, 4] },
  { code: 'ES', dial: '34', trunk: false, patterns: [/^[6-9]\d{8}$/], groups: () => [3, 2, 2, 2] },
  { code: 'IT', dial: '39', trunk: false, patterns: [/^3\d{8,9}$/, /^0\d{5,10}$/], groups: () => [3, 3, 4] },
  { code: 'PT', dial: '351', trunk: false, patterns: [/^[239]\d{8}$/], groups: () => [3, 3, 3] },
  { code: 'CH', dial: '41', trunk: true, patterns: [/^[1-9]\d{8}$/], groups: () => [2, 3, 2, 2] },
  { code: 'AT', dial: '43', trunk: true, patterns: [/^[1-9]\d{5,12}$/], groups: () => [3] },
  { code: 'DK', dial: '45', trunk: false, patterns: [/^[2-9]\d{7}$/], groups: () => [2, 2, 2, 2] },
  { code: 'US', dial: '1', trunk: false, patterns: [/^[2-9]\d{2}[2-9]\d{6}$/], groups: () => [3, 3, 4] },
];

export const DEFAULT_PHONE_COUNTRY = 'BE';
export const OTHER_PHONE_COUNTRY = 'OTHER';

const ALLOWED_CHARS = /^\+?[\d\s().\-/]+$/;

function group(digits, sizes) {
  const parts = [];
  let i = 0;
  for (const size of sizes) {
    if (i >= digits.length) break;
    parts.push(digits.slice(i, i + size));
    i += size;
  }
  // Rest: in blokken van 3 (laatste blok mag 2-4 cijfers zijn)
  while (i < digits.length) {
    const left = digits.length - i;
    const take = left === 4 ? 4 : left <= 3 ? left : 3;
    parts.push(digits.slice(i, i + take));
    i += take;
  }
  return parts.join(' ');
}

function isRepeatingDigit(digits) {
  return /^(\d)\1+$/.test(digits);
}

function matchesCountry(country, nsn) {
  return country.patterns.some((re) => re.test(nsn)) && !isRepeatingDigit(nsn);
}

function stripTrunk(country, digits) {
  return country.trunk && digits.startsWith('0') ? digits.slice(1) : digits;
}

function findCountryByDial(digits) {
  // Langste landcode eerst (352 vóór 3x, enz.)
  return [...PHONE_COUNTRIES]
    .sort((a, b) => b.dial.length - a.dial.length)
    .find((c) => digits.startsWith(c.dial));
}

function build(country, nsn) {
  const sizes = country.groups(nsn);
  const national = group(nsn, sizes);
  return {
    valid: true,
    country: country.code,
    e164: `+${country.dial}${nsn}`,
    national,
    formatted: `+${country.dial} ${national}`,
  };
}

/**
 * Controleert een ingetypt telefoonnummer.
 * @param {string} input    wat de gebruiker typte (met of zonder landcode)
 * @param {string} countryCode  gekozen land ("BE", ...), of "OTHER"
 * @returns {{valid:boolean, reason?:string, country?:string, e164?:string, national?:string, formatted?:string}}
 */
export function parsePhone(input, countryCode = DEFAULT_PHONE_COUNTRY) {
  const raw = String(input || '').trim();
  if (!raw) return { valid: false, reason: 'empty' };
  if (!ALLOWED_CHARS.test(raw)) return { valid: false, reason: 'invalid' };

  let digits = raw.replace(/\D/g, '');
  let international = raw.startsWith('+');
  if (!international && digits.startsWith('00')) {
    international = true;
    digits = digits.slice(2);
  }

  if (international) {
    const country = findCountryByDial(digits);
    if (country) {
      let nsn = digits.slice(country.dial.length);
      // "+32 (0)470 ..." : de 0 na de landcode hoort er niet bij
      nsn = stripTrunk(country, nsn);
      return matchesCountry(country, nsn) ? build(country, nsn) : { valid: false, reason: 'invalid' };
    }
    // Land buiten onze lijst: enkel algemene internationale controle (E.164)
    if (/^[1-9]\d{7,14}$/.test(digits) && !isRepeatingDigit(digits)) {
      return {
        valid: true,
        country: OTHER_PHONE_COUNTRY,
        e164: `+${digits}`,
        national: group(digits, [3]),
        formatted: `+${group(digits, [3])}`,
      };
    }
    return { valid: false, reason: 'invalid' };
  }

  // Zonder landcode: dan geldt het gekozen land
  if (countryCode === OTHER_PHONE_COUNTRY) return { valid: false, reason: 'invalid' };
  const country = PHONE_COUNTRIES.find((c) => c.code === countryCode);
  if (!country) return { valid: false, reason: 'invalid' };
  const nsn = stripTrunk(country, digits);
  return matchesCountry(country, nsn) ? build(country, nsn) : { valid: false, reason: 'invalid' };
}
